// @ts-nocheck
import sortBy from 'lodash/sortBy';
import { isValueEmpty } from '../core';
import { getLabelPathFromLabelConfig } from '../labels';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  getFilteredEntityTypes,
} from '../../const/CommonConst';

// These keys are defined in frontend code for these entity types, so from backend it is different
export const getEntityTypeKey = (entityType, requestType) => {
  if (entityType === 'ModelAssociation') {
    return 'ModelAssociation';
  }

  if (entityType === 'ModelInventory') {
    switch (requestType) {
      case 'ModelRequest':
        return 'modelRequest';
      case 'AccessRequest':
        return 'accessRequest';
      case 'UseCase':
        return 'useCase';
      case 'Query':
        return 'query';
      default:
        return 'modelInventory';
    }
  }
  return 'modelInventory';
};

export const getEntityTypes = (entityType, requestType) => {
  if (entityType === 'ModelAssociation') {
    return entityType;
  }
  if (entityType === 'ModelInventory') {
    if (requestType === 'Normal') {
      return entityType;
    }
    return requestType;
  }
};

/**
 * Returns the label for a given entity type and request type.
 *
 * @param {string} entityType - The type of entity (e.g. 'ModelInventory', 'ModelAssociation').
 * @param {string} [requestType='Normal'] - The type of request (e.g. 'ModelRequest', 'Normal').
 * @returns {string} The label for the given entity and request type.
 */
export const getEntityTypeLabel = (entityType, requestType = 'Normal') => {
  const entityTypeKey = getEntityTypes(entityType, requestType);

  if (isValueEmpty(entityTypeKey)) {
    return entityType || '-';
  }

  return getLabelPathFromLabelConfig(
    `TEMPLATE_TYPES.${entityTypeKey}`,
    'label',
    entityTypeKey
  );
};

/**
 * Builds the flat permission key used for template-level inventory permissions.
 *
 * Examples:
 * - ModelAssociation + "Subprocess Template" -> Inventory_modelAssociation_template_Subprocess Template
 * - ModelInventory + ModelRequest + "Assessment Template" -> Inventory_modelRequest_template_Assessment Template
 *
 * @param {string} templateName - Template/entity name from the association config.
 * @param {string} entityType - Backend entity type, such as ModelAssociation or ModelInventory.
 * @param {string} requestType - Request type used to resolve ModelInventory variants.
 * @returns {string} Permission key present in the user permissions object.
 */
export const getDynamicEntityPermissionKey = (
  templateName,
  entityType,
  requestType
) => {
  if (!templateName || !entityType) {
    return '';
  }
  const entityTypeKey = getEntityTypeKey(entityType, requestType);
  const entityPermissionKey =
    String(entityTypeKey).charAt(0).toLowerCase() +
    String(entityTypeKey).slice(1);

  return `Inventory_${entityPermissionKey}_template_${templateName}`;
};

/**
 * Filters association templates by template-level permissions.
 *
 * Associations with permission "None" are hidden from issue-type dropdowns.
 * "View", "Manage", and missing permissions are kept visible; action buttons
 * separately decide whether the selected association can be managed.
 *
 * @param {Array<object>} associationList - Associations for the current issue type.
 * @param {object} permissions - Flat permissions object from Redux/user API.
 * @returns {Array<object>} Associations that should remain visible.
 */
export const getPermittedAssociationsByPermission = (
  associationList = EMPTY_ARRAY,
  permissions = EMPTY_OBJECT
) => {
  return (associationList || []).filter((entity) => {
    const permissionKey = getDynamicEntityPermissionKey(
      entity?.entity_name,
      entity?.entity_type,
      entity?.request_type
    );

    return permissions?.[permissionKey] !== 'None';
  });
};

export const getTemplateType = (newValue) => {
  switch (newValue) {
    case 'ModelInventory':
    case 'ModelRequest':
      return {
        tempType: 'ModelInventory',
        reqType: newValue === 'ModelRequest' ? 'ModelRequest' : 'Normal',
      };
    case 'ModelAssociation':
      return {
        tempType: 'ModelAssociation',
        reqType: 'Normal',
      };
    case 'UseCase':
      return {
        tempType: 'ModelInventory',
        reqType: 'UseCase',
      };
    default:
      return {
        tempType: 'ModelInventory',
        reqType: 'Normal',
      };
  }
};

/**
 * Prepare attributes with group/section metadata for autocomplete components
 * @param {Object} templateData - Template data containing attributes and sections
 * @param {Array} excludedDataTypes - Array of data types to exclude
 * @returns {Array} Attributes with group_name and group_id metadata
 */
export const getTemplateAttributes = (
  templateData,
  excludedDataTypes = [],
  excludeSystemAttribute = false
) => {
  if (!templateData) {
    return [];
  }

  // systemAtt and templateAtts are added to handle attributes during dynamic key generation
  const systemAtt =
    (templateData?.system_attributes || []).map((attr) => ({
      name: attr,
      label: attr,
      value: attr,
      data_type: 'System',
      group_name: 'System Attributes',
      group_id: 'system_attributes',
    })) || [];

  const templateAtts =
    (templateData?.template_attributes || []).map((att) => ({
      ...att,
      label: att?.label || att?.name,
      value: att?.id || att?.name,
      group_name: att?.section || 'Basic Details',
      group_id: att?.section || 'basic_details',
    })) || [];

  const basicInfoAtt =
    templateData?.attributes?.map((att) => ({
      ...att,
      label: att?.label || att?.name,
      value: att?.id || att?.name,
      name: att?.name,
      id: att?.id,
      group_name: 'Basic Details',
      group_id: 'basic_details',
    })) || [];

  const sectionAtt =
    templateData?.sections?.flatMap((section) =>
      section?.attributes?.map((att) => ({
        ...att,
        label: att?.label || att?.name,
        value: att?.id || att?.name,
        name: att?.name,
        id: att?.id,
        group_name: section?.section_name,
        group_id: section?.section_id || section?.section_name,
      }))
    ) || [];

  const allAttributes = [
    ...systemAtt,
    ...templateAtts,
    ...basicInfoAtt,
    ...sectionAtt,
  ];

  if (excludedDataTypes.length === 0 && !excludeSystemAttribute) {
    return allAttributes;
  }

  const added = new Set();
  const filteredAttributes = [];

  allAttributes.forEach((att) => {
    if (
      !added.has(att?.name) &&
      !excludedDataTypes.includes(att?.data_type) &&
      (att?.is_editable === true ||
        att?.is_derived === true ||
        att?.data_type === 'System' ||
        templateData.template_attributes) &&
      (!excludeSystemAttribute || att?.attribute_origin !== 'common')
    ) {
      added.add(att?.name);
      filteredAttributes.push(att);
    }
  });

  return filteredAttributes;
};

export const getTemplateEntityTypeLabel = (entityType, requestType) => {
  const entityTypeKey = getEntityTypes(entityType, requestType);
  return getLabelPathFromLabelConfig(
    `TEMPLATE_TYPES.${entityTypeKey}`,
    'featureLabel',
    entityTypeKey
  );
};

// Adds the entity type label to each template so templates can be shown in groups.
// Templates without a resolvable entity type are omitted to avoid misleading buckets.
export const groupTemplateList = (templateList = EMPTY_ARRAY) => {
  const entityTypes = getFilteredEntityTypes() || [];
  const groupedTemplates =
    templateList
      ?.map((temp) => {
        const matchedEntityType = entityTypes.find(
          (type) => type?.value === temp?.entity_type
        );
        if (!matchedEntityType?.label) {
          return null;
        }

        return {
          ...temp,
          label: temp?.entity_name,
          value: temp?.id,
          type: matchedEntityType?.label,
        };
      })
      ?.filter(Boolean) || [];
  return sortBy(groupedTemplates, [(item) => item?.type?.toLowerCase() || '']);
};

/**
 * Filters sections based on permissions, conditional sections, and workflow groups.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.sections - The array of sections to filter.
 * @param {Object} params.sectionPermissions - The section permissions object from Redux.
 * @param {string} params.entityCurrentGroupId - The current entity group ID from Redux.
 * @param {string} params.entitySelectedGroupId - The selected entity group ID (optional).
 * @param {boolean} [params.excludeViewPermissionSections=false] - When true, also
 *   drops sections whose permission is `view` (read-only). Used by validation/error-dialog
 *   callers so read-only sections are not validated; rendering callers leave it off.
 * @returns {Array} - The filtered array of sections.
 *
 * @example
 * const filteredSections = filterSectionsByPermissions({
 *   sections: formDetails?.sections,
 *   sectionPermissions,
 *   entityCurrentGroupId,
 *   entitySelectedGroupId,
 * });
 */
export const filterSectionsByPermissions = ({
  sections = EMPTY_ARRAY,
  sectionPermissions = EMPTY_OBJECT,
  entityCurrentGroupId = '',
  entitySelectedGroupId = '',
  selectedStateNode = '',
  isNodeEdit = false,
  excludeViewPermissionSections = false,
}) => {
  return (
    sections?.filter((section) => {
      const secID = section?.section_id;
      if (
        excludeViewPermissionSections &&
        sectionPermissions?.section_permissions?.[secID] === 'view'
      ) {
        return false;
      }

      if (isNodeEdit) {
        if (
          sectionPermissions?.section_permissions?.[secID] !== 'manage' &&
          sectionPermissions?.section_permissions?.[secID] !== 'view'
        ) {
          return false;
        }
      }

      if (
        !isValueEmpty(entitySelectedGroupId?.groupId) &&
        section?.allow_grouping === true
      ) {
        return section?.show_in_workflow_group?.includes(
          entitySelectedGroupId?.groupId
        );
      }

      if (!isValueEmpty(selectedStateNode)) {
        return sectionPermissions?.section_permissions?.[secID] !== 'none';
      }
      // do not render a section when condition boolean is false
      if (sectionPermissions?.conditional_sections?.[secID] === false) {
        return false;
      }
      // do not render a section when its permission is none
      if (sectionPermissions?.section_permissions?.[secID] === 'none') {
        return false;
      }
      if (
        section?.allow_grouping === true &&
        !isValueEmpty(entityCurrentGroupId)
      ) {
        return section?.show_in_workflow_group?.includes(entityCurrentGroupId);
      }
      return true;
    }) || []
  );
};

export const getTaskEntityType = (entityType, requestType) => {
  const resolved = getEntityTypes(entityType, requestType);
  return resolved || entityType;
};

export const checkEntityTypeMatch = (
  taskEntityType,
  selectedTypes,
  entityTypes
) => {
  if (!selectedTypes || selectedTypes.length === 0) {
    return true;
  }
  return selectedTypes.some((selectedVal) => {
    const matchedType = entityTypes?.find((t) => t.value === selectedVal);
    if (matchedType) {
      return (
        taskEntityType === matchedType.value ||
        taskEntityType?.toLowerCase() === matchedType.value?.toLowerCase() ||
        taskEntityType?.toLowerCase() === matchedType.label?.toLowerCase()
      );
    }
    return (
      taskEntityType === selectedVal ||
      taskEntityType?.toLowerCase() === selectedVal?.toLowerCase()
    );
  });
};
